import { memo, useCallback, useEffect, useRef } from 'react';

import { createCurrentEvent } from '@/common/analytics/gtm/Homepage/currentEventsAnalytics';
import { trackGA4SelectPromotion } from '@/common/analytics/gtm/shared/trackPromotionClick';
import { DefaultTrackPromotionEvent } from '@/common/analytics/gtm/shared/trackViewPromotion';
import Badge from '@/common/components/Badge/Badge';
import { CounterToBadge } from '@/common/components/CounterToBadge/CounterToBadge';
import EventTile from '@/common/components/EventTile/EventTile';
import { useImpression } from '@/common/impressions';
import { HomepageCurrentEvent } from 'generatedBfw/static';

import styles from './currentEvents.module.scss';

interface CurrentEventTileProps {
  event: HomepageCurrentEvent;
  index: number;
  className?: string;
  isLazyLoaded?: boolean;
  setElementHeight?: (height: number) => void;
}

const CurrentEventTile = ({
  event,
  index,
  className,
  isLazyLoaded = true,
  setElementHeight,
}: CurrentEventTileProps) => {
  const ref = useRef<HTMLLIElement | null>(null);
  const analyticsEvent = createCurrentEvent(event, index);
  const { setOnHold } = useImpression<DefaultTrackPromotionEvent>({
    ref: ref,
    message: analyticsEvent,
    initialOnHold: true,
  });

  useEffect(() => {
    if (!setElementHeight) return;

    const handleResize = () => {
      const height = ref.current?.getBoundingClientRect().height;
      if (height) setElementHeight(height);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [setElementHeight]);

  const initRef = useCallback(
    (el: HTMLLIElement | null) => {
      ref.current = el;
      if (!setElementHeight) return;
      const height = el?.getBoundingClientRect().height;
      if (height) setElementHeight(height);
    },
    [setElementHeight],
  );

  return (
    <li className={className} key={event.id} ref={initRef}>
      <EventTile
        {...event}
        badge={event.badge && <Badge type={event.badge} />}
        className={styles.event}
        imageBadge={event.endsAt ? <CounterToBadge endsAt={event.endsAt} /> : undefined}
        imageHeight={144}
        imageWidth={228}
        isLazyLoaded={isLazyLoaded}
        onClick={() => trackGA4SelectPromotion<DefaultTrackPromotionEvent>(analyticsEvent)}
        setOnHold={setOnHold}
      />
    </li>
  );
};

export default memo(CurrentEventTile);
